import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { BodyCopy } from "../components/BodyCopy";
import { Toast } from "../components/Toast";
import {
  BankIcon,
  Button,
  CardIcon,
  CashSpecialIcon,
  ExchangeIcon,
  Layout,
  SupportIcon,
  Title,
  WalletIcon,
  WarningIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlagCircle } from "../components/FlagCircle";
import { QuoteInfo } from "../components/QuoteInfo";
import { PayoutInfo } from "../components/PayoutInfo";
import {
  formatBankAccountLabel,
  looksLikePayerCode,
} from "../utils/bankAccountLabel";
import { FooterDetailSummary } from "../components/FooterDetailSummary";
import { Popup } from "../components/Popup";
import {
  SelectPayoutMethodSlot,
  type FlatPayoutPick,
} from "../components/SelectPayoutMethodSlot";
import {
  PayoutDetailsSlot,
  type PayoutDetailsResult,
} from "../components/PayoutDetailsSlot";
import {
  SelectFundingMethodSlot,
  type FlatFundingPick,
} from "../components/SelectFundingMethodSlot";
import { AmountOutOfRangeInfo } from "../components/AmountOutOfRangeInfo";
import { RecipientDetailsSlot } from "../components/RecipientDetailsSlot";
import { SelectDestinationCountrySlot } from "../components/SelectDestinationCountrySlot";
import { SelectOriginCountrySlot } from "../components/SelectOriginCountrySlot";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { useStore } from "../store/globalState";
import { FirstSendWarning } from "../components/first-send/FirstSendWarning";
import { useFirstSendWarning } from "../hooks/useFirstSendWarning";
import { shouldPromptPhone } from "../utils/phoneGate";
import { ensureSmsConsent } from "../components/notifications/smsConsent";
import { useSessionStore } from "../store/useSessionStore";
import { useSendStore } from "../store/useSendStore";
import { useComposeStore } from "../store/useComposeStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { usePreQuote } from "../hooks/usePreQuote";
import { useQuote } from "../hooks/useQuote";
import { useFundingMethodLimits } from "../hooks/useFundingMethodLimits";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import {
  useCatalogCountries,
  useCatalogOriginCountries,
  useCatalogPayers,
} from "../hooks/useCatalog";
import { catalogPayerDisplayName } from "../utils/payerCode";
import {
  corridorCurrencyCodesByDestination,
  currencyForCountry,
  expandCountryCurrencyOptions,
  mergeCatalogWithCorridorCurrencies,
  pickDefaultDestinationCurrency,
  type CountryCapability,
  type OriginCountryCapability,
} from "../api/catalog";
import {
  checkCipReadiness,
  getWidgetApiError,
  isDeadLinkError,
} from "../services/api";
import { PayoutMethodType } from "../interfaces/payout";
import {
  isFundingInstrumentReady,
  type FundingInstrumentSaved,
} from "../interfaces/funding";
import { prepareFundingRail } from "../utils/prepare-funding-rail";
import { UnavailableFundingCardSheet } from "../components/UnavailableFundingCardSheet";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import {
  isFundingCardBlocked,
  resolveFundingCardFromInstrument,
} from "../utils/resolve-funding-card";
import {
  reasonFromCardPayCode,
  type UnavailableCardReason,
} from "../utils/card-funding-availability";
import {
  catalogFundingMethodOf,
  type AddCardClassNotice,
} from "../utils/resolve-add-card-funding-class";
import {
  preparePayoutRail,
  sanitizePayoutAfterPrepare,
} from "../utils/prepare-payout-rail";
import { isPayoutCardValidationStale } from "../utils/pre-validate-payout-card";
import { CvvPrepareGate } from "../components/CvvPrepareGate";
import {
  FUNDING_METHOD_CATEGORY,
  availableFundingMethods,
  methodEstimateFee,
  resolveSelectedEstimateOption,
  type FundingMethod,
} from "../utils/catalog";
import type { PreQuoteInput, EstimateOption } from "../api/pricing";
import { amountFormatter } from "../utils";
import {
  compareSendToBound,
  corridorsForFundingMethod,
  skusForCorridors,
} from "../utils/fundingLimits";
import { resolveComposeSendAmount } from "../utils/resolveComposeSendAmount";
import masterCardLogo from "../assets/master_card_icon.svg";
import visaLogo from "../assets/visa_color_icon.svg";
import { getEvents, safeCapture } from "../analytics";
import { quoteMatchesSelection } from "../utils/quoteMatchesSelection";
import { useQuoteLoadingTimedOut } from "../hooks/useQuoteLoadingTimedOut";
import {
  resolveInitialCorridor,
  seedDestinationCountry,
  seedOriginCountry,
  supportedDestinations,
  supportedOrigins,
} from "../utils/corridors";
import {
  compatibleBookDestinations,
  shouldClearPayoutOnRecipientChange,
  type CompatibleBookDestination,
} from "../utils/recipient-destinations";
import { countryDisplayName } from "../utils/country-display";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


/** Payout methods this flow can compose, mapped from catalog vocabulary. */
const SUPPORTED_PAYOUT_METHODS: PayoutMethodType[] = [
  PayoutMethodType.BANK_ACCOUNT,
  PayoutMethodType.CASH_PICKUP,
  PayoutMethodType.PUSH_TO_CARD,
  PayoutMethodType.MOBILE_WALLET,
];

const isSupportedPayoutMethod = (value: string): value is PayoutMethodType =>
  (SUPPORTED_PAYOUT_METHODS as string[]).includes(value);

const sheetCardFromNotice = (
  card: AddCardClassNotice["card"]
): ICardPaymentMethod => ({
  paymentMethodId: card.paymentMethodId,
  brand: card.brand,
  last4: card.last4,
  expiryMonth: "",
  expiryYear: "",
  enabled: true,
  isDefault: false,
  fundingMethod: card.fundingMethod,
  productClass: card.productClass,
});

const toOriginPickerRows = (
  codes: string[],
  catalog: OriginCountryCapability[] | null | undefined,
  locale: string,
  pinnedOrigin?: string,
  pinnedCurrency?: string
): CountryCapability[] =>
  codes
    .map((code) => {
      const row = catalog?.find((c) => c.countryCode === code);
      const currencyCode =
        code === pinnedOrigin ? pinnedCurrency : undefined;
      if (row) {
        return {
          countryCode: row.countryCode,
          countryName: countryDisplayName(row.countryCode, locale),
          currencyCode,
          supportedByCorridors: row.supportedByCorridors,
          effectiveStatus: row.effectiveStatus,
        };
      }
      return {
        countryCode: code,
        countryName: countryDisplayName(code, locale),
        currencyCode,
        supportedByCorridors: [],
        effectiveStatus: "AVAILABLE" as const,
      };
    })
    .filter((c) => c.effectiveStatus === "AVAILABLE");

type PayoutStep = "method" | "details";

/** Sequential compose rows below the amount input. */
type ComposeStep = "funding" | "payout" | "recipient";

const COMPOSE_STEP_ORDER: ComposeStep[] = ["funding", "recipient", "payout"];

/**
 * `primaryMesh` is usually a gradient string, which is invalid for `color` and
 * `border-color`. The outlined icon in the rate pill needs a solid value, so it
 * falls back to the gradient's first stop.
 */
const solidStop = (color: string) =>
  color.match(/#[0-9a-f]{3,8}|rgba?\([^)]*\)/i)?.[0] ?? color;

/**
 * Compose-a-send screen. The sender enters how much they PAY (source currency,
 * send-anchored), can change the destination country, picks how they FUND the
 * transfer, and arms the recipient's payout method + minimal recipient data.
 * A debounced pre-quote drives available methods and the chosen SKU. Once the
 * SKU is pinned, a binding quote auto-loads (m2m/fund-request parity);
 * confirming persists it, gates on CIP, then sends.
 */
export const TransferPage = memo(() => {
  const { t, i18n } = useTranslation();
  const { theme, amountState } = useStore();
  const { session } = useSessionStore();
  const { setDraft, setQuoteState } = useSendStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();
  const navigate = useNavigate();
  const location = useLocation();
  const cardClassNotice = (
    location.state as { cardClassNotice?: AddCardClassNotice } | null
  )?.cardClassNotice;
  const cardClassNoticeHandledRef = useRef(false);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("route_amount"));
    safeCapture(getEvents().SCREEN_VIEW("summary"));
  }, []);

  const { amount, anchor: amountAnchor } = amountState;
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    dropzoneColors,
    bannerHighlightBackground,
    bannerHighlightColor,
    bannerHighlightIconColors,
  } = colorConfig as typeof colorConfig & {
    bannerHighlightBackground?: string;
    bannerHighlightColor?: string;
    bannerHighlightIconColors?: string[];
  };

  const linkId = linkIdFromRoute || session.data?.linkId;
  const userStatus = session.data?.userStatus;
  const canSend = !userStatus?.blocked && !userStatus?.suspended;
  // Revalidates on mount; refresh when opening the picker or after register.
  const {
    cards: savedCards,
    bankAccounts,
    loading: paymentMethodsLoading,
    refresh: refreshPaymentMethods,
  } = usePaymentMethods(linkId);

  const config = session.data?.config;
  const corridors = config?.corridors ?? [];
  const initialCorridor = resolveInitialCorridor(
    corridors,
    config?.defaultCorridor
  );

  const {
    originCountry: storedOrigin,
    setOriginCountry,
    destinationCountry: storedDestination,
    setDestinationCountry,
    destinationCurrency: storedCurrency,
    setDestinationCurrency,
    funding,
    setFunding,
    fundingInstrument,
    setFundingInstrument,
    selection,
    setSelection,
    recipient,
    setRecipient,
    bookDestinations,
    setBookDestinations,
  } = useComposeStore();

  const seededOrigin =
    seedOriginCountry(corridors, config?.defaultCorridor) ??
    supportedOrigins(corridors)[0] ??
    "US";
  const seededDestination = seedDestinationCountry(
    corridors,
    config?.defaultCorridor
  );
  const originCountry = storedOrigin ?? seededOrigin;
  const destinationCountry = storedDestination ?? seededDestination ?? "";
  const pinnedDestination =
    initialCorridor?.destinationCountryCode ?? seededDestination ?? "";

  useEffect(() => {
    if (storedOrigin == null && seededOrigin) {
      setOriginCountry(seededOrigin);
    }
  }, [storedOrigin, seededOrigin, setOriginCountry]);

  useEffect(() => {
    if (storedDestination == null && seededDestination) {
      setDestinationCountry(seededDestination);
    }
  }, [storedDestination, seededDestination, setDestinationCountry]);

  const corridorOriginCodes = useMemo(() => {
    const fromCorridors = supportedOrigins(corridors);
    if (fromCorridors.length > 0) return fromCorridors;
    return [seededOrigin];
  }, [corridors, seededOrigin]);

  const { data: originCatalog } = useCatalogOriginCountries();
  const originCountriesForPicker = useMemo(
    () =>
      toOriginPickerRows(
        corridorOriginCodes,
        originCatalog,
        i18n.language,
        config?.defaultCorridor?.originCountryCode,
        config?.sourceCurrency
      ),
    [
      corridorOriginCodes,
      originCatalog,
      i18n.language,
      config?.defaultCorridor?.originCountryCode,
      config?.sourceCurrency,
    ]
  );

  const { data: countries } = useCatalogCountries({
    originCountryCode: originCountry,
  });

  const corridorDestinations = useMemo(() => {
    const fromCorridors = supportedDestinations(corridors, originCountry);
    if (fromCorridors.length > 0) return fromCorridors;
    return seededDestination ? [seededDestination] : [];
  }, [corridors, originCountry, seededDestination]);

  const corridorCodesByDest = useMemo(
    () => corridorCurrencyCodesByDestination(corridors),
    [corridors]
  );

  const destinationCountriesForPicker = useMemo(
    () =>
      expandCountryCurrencyOptions(
        mergeCatalogWithCorridorCurrencies(
          (countries ?? []).map((c) => ({
            ...c,
            countryName: countryDisplayName(c.countryCode, i18n.language),
          })),
          corridorDestinations,
          corridorCodesByDest,
          (code) => countryDisplayName(code, i18n.language)
        )
      ),
    [countries, corridorDestinations, corridorCodesByDest, i18n.language]
  );

  const catalogDestination = useMemo(() => {
    const rows = destinationCountriesForPicker.filter(
      (o) => o.countryCode === destinationCountry
    );
    if (rows.length === 0) {
      return (countries ?? []).find((c) => c.countryCode === destinationCountry);
    }
    return {
      countryCode: destinationCountry,
      countryName: rows[0].countryName,
      currencyCodes: rows.map((r) => r.currencyCode),
      supportedByCorridors: rows[0].supportedByCorridors,
      effectiveStatus: rows[0].effectiveStatus,
    };
  }, [destinationCountriesForPicker, countries, destinationCountry]);

  const baseSourceCurrency = useMemo(() => {
    if (
      originCountry === config?.defaultCorridor?.originCountryCode &&
      config?.sourceCurrency
    ) {
      return config.sourceCurrency;
    }
    const fromPicker = originCountriesForPicker.find(
      (c) => c.countryCode === originCountry
    );
    return currencyForCountry(fromPicker) ?? "USD";
  }, [
    originCountry,
    config?.defaultCorridor?.originCountryCode,
    config?.sourceCurrency,
    originCountriesForPicker,
  ]);

  useEffect(() => {
    if (!destinationCountry) return;
    const codes =
      catalogDestination?.currencyCodes ??
      (catalogDestination
        ? [
            catalogDestination.currencyCode,
            catalogDestination.currency,
          ].filter((c): c is string => Boolean(c))
        : []);
    const normalized = codes.map((c) => c.toUpperCase());
    if (normalized.length === 0) return;
    const pinned =
      destinationCountry === pinnedDestination
        ? config?.destinationCurrency
        : undefined;
    const preferred = pickDefaultDestinationCurrency(
      catalogDestination,
      pinned
    );
    if (!preferred) return;
    if (storedCurrency == null || !normalized.includes(storedCurrency)) {
      setDestinationCurrency(preferred);
    }
  }, [
    destinationCountry,
    catalogDestination,
    storedCurrency,
    pinnedDestination,
    config?.destinationCurrency,
    setDestinationCurrency,
  ]);

  const destinationCurrency =
    storedCurrency ??
    pickDefaultDestinationCurrency(
      catalogDestination,
      destinationCountry === pinnedDestination
        ? config?.destinationCurrency
        : undefined
    ) ??
    "USD";

  const [popup, setPopup] = useState<
    | "origin"
    | "funding"
    | "payout"
    | "recipient"
    | "destination"
    | "amountLimit"
    | null
  >(null);
  const [payoutStep, setPayoutStep] = useState<PayoutStep>("method");
  const [pendingMethod, setPendingMethod] = useState<PayoutMethodType | null>(
    null
  );
  const [shake, setShake] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showExcratePopup, setShowExcratePopup] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [cvvGate, setCvvGate] = useState<{
    last4: string;
    brand?: string;
    funding: FundingInstrumentSaved;
    error: string | null;
  } | null>(null);
  const [blockedFundingCard, setBlockedFundingCard] =
    useState<ICardPaymentMethod | null>(null);
  const [blockedRailCards, setBlockedRailCards] = useState<
    ReadonlyMap<string, UnavailableCardReason>
  >(() => new Map());
  const [classChangedToast, setClassChangedToast] = useState<
    "DEBIT_CARD" | "CREDIT_CARD" | null
  >(null);

  useEffect(() => {
    const cardId =
      fundingInstrument?.source === "saved"
        ? fundingInstrument.paymentMethodId
        : null;
    if (!cardId || !blockedRailCards.has(cardId)) return;
    setFundingInstrument(null);
  }, [blockedRailCards, fundingInstrument, setFundingInstrument]);

  const handleOriginChange = useCallback(
    (code: string) => {
      if (code !== originCountry) {
        setFunding(null);
        setSelection(null);
        setFundingInstrument(null);
        setPayoutStep("method");
        setPendingMethod(null);

        const validDestinations = supportedDestinations(corridors, code);
        if (
          validDestinations.length > 0 &&
          !validDestinations.includes(destinationCountry)
        ) {
          setDestinationCountry(validDestinations[0]);
        }
      }
      setOriginCountry(code);
      safeCapture(getEvents().ORIGIN_SELECTED, {
        from_country: code,
        to_country: destinationCountry,
      });
    },
    [
      originCountry,
      destinationCountry,
      corridors,
      setFunding,
      setSelection,
      setFundingInstrument,
      setDestinationCountry,
      setOriginCountry,
    ]
  );

  // Changing destination invalidates funding + payout availability. Reset from
  // the picker handler (not an effect keyed on destinationCountry, which would
  // also fire on remount from add-card and wipe the preserved compose).
  const handleDestinationChange = useCallback(
    (countryCode: string, currencyCode: string) => {
      if (
        countryCode !== destinationCountry ||
        currencyCode !== destinationCurrency
      ) {
        setFunding(null);
        setSelection(null);
        setBookDestinations([]);
        setPayoutStep("method");
        setPendingMethod(null);
      }
      setDestinationCountry(countryCode);
      setDestinationCurrency(currencyCode);
      safeCapture(getEvents().DESTINATION_SELECTED, {
        from_country: originCountry,
        to_country: countryCode,
        to_currency: currencyCode,
      });
    },
    [
      destinationCountry,
      destinationCurrency,
      originCountry,
      setFunding,
      setSelection,
      setBookDestinations,
      setDestinationCountry,
      setDestinationCurrency,
    ]
  );

  const pinFunding = useCallback(
    (m: FundingMethod) => {
      if (m !== funding) {
        setSelection(null);
        setPayoutStep("method");
        setPendingMethod(null);
      }
      setFunding(m);
      safeCapture(getEvents().FUNDING_SELECTED, { funding_method: m });
    },
    [funding, setFunding, setSelection]
  );

  const handleFundingPick = useCallback(
    (pick: FlatFundingPick) => {
      if (pick.kind === "add") {
        pinFunding(pick.method);
        setFundingInstrument(null);
        setPopup(null);
        redirectTo(APP_ROUTES.ADD_CARD_PAGE);
        return;
      }
      if (pick.kind === "cash") {
        pinFunding("CASH_DEPOSIT");
        setFundingInstrument(null);
        setPopup(null);
        return;
      }
      if (pick.kind === "method") {
        pinFunding(pick.method);
        setFundingInstrument(null);
        setPopup(null);
        return;
      }
      if (pick.kind === "account") {
        pinFunding("ACH");
        setFundingInstrument(null);
        setPopup(null);
        return;
      }
      const cardMethod = catalogFundingMethodOf(pick.card.fundingMethod);
      if (!cardMethod) {
        setBlockedFundingCard(pick.card);
        setPopup(null);
        return;
      }
      pinFunding(cardMethod);
      setFundingInstrument({
        source: "saved",
        paymentMethodId: pick.card.paymentMethodId,
        brand: pick.card.brand,
        last4: pick.card.last4,
        fundingMethod: cardMethod,
      });
      safeCapture(getEvents().CARD_REGISTERED, {
        success: true,
        card_brand: pick.card.brand,
        funding_payment_method_id: pick.card.paymentMethodId,
        last4: pick.card.last4,
      });
      setPopup(null);
    },
    [pinFunding, redirectTo, setFundingInstrument]
  );

  const prepareErrorMessage = useCallback(
    (code: string | undefined): string => {
      switch (code) {
        case "PROVISION_FAILED":
          return t("transfer.prepare.errorProvision");
        case "CVV_REQUIRED":
          return t("transfer.prepare.errorCvv");
        case "REGISTER_FAILED":
          return t("transfer.prepare.errorRegister");
        case "READINESS_FAILED":
          return t("transfer.prepare.errorReadiness");
        case "VGS_CONFIG_UNAVAILABLE":
          return t("vgs.configUnavailable");
        case "CARD_PUSH_STALE":
          return t("payout.card.corridorChanged");
        case "STALE_PAYMENT_METHOD":
        case "STUB_ID_REQUIRED":
          return t("transfer.prepare.errorStalePayoutCard");
        default:
          return t("transfer.prepare.errorGeneric");
      }
    },
    [t]
  );

  // Destination-card dry-run is corridor-scoped: clear it when the route changes.
  useEffect(() => {
    const current = useComposeStore.getState().selection;
    if (!current || current.method !== PayoutMethodType.PUSH_TO_CARD) {
      return;
    }
    const payout = current.payout;
    if (!payout.cvsValidationId && !payout.validatedFor) return;
    if (
      !isPayoutCardValidationStale(
        payout.validatedFor,
        originCountry,
        destinationCountry,
      )
    ) {
      return;
    }
    setSelection({
      ...current,
      payout: {
        ...payout,
        cvsValidationId: undefined,
        numberToken: undefined,
        validatedFor: undefined,
      },
    });
  }, [originCountry, destinationCountry, setSelection]);

  const numericAmount = amount ? parseFloat(amount) : NaN;
  const hasAmount = !isNaN(numericAmount) && numericAmount > 0;

  // Discovery: unscoped facets (+ options fallback). Facets are pre-collapse so
  // every quotable funding method appears even when DEBIT and CASH tie on price.
  const discoveryPreQuoteInput = useMemo<PreQuoteInput | null>(() => {
    if (!hasAmount) return null;
    return {
      originCountryCode: originCountry,
      destinationCountryCode: destinationCountry,
      amount: numericAmount,
      anchor: amountAnchor,
      destinationCurrency,
      sourceCurrency: baseSourceCurrency,
      vary: "fundingMethod",
      include: ["facets", "options", "summary"],
    };
  }, [
    hasAmount,
    numericAmount,
    amountAnchor,
    originCountry,
    destinationCountry,
    destinationCurrency,
    baseSourceCurrency,
  ]);

  const {
    result: discoveryPreQuote,
    loading: discoveryPreQuoteLoading,
    error: discoveryPreQuoteError,
  } = usePreQuote(discoveryPreQuoteInput);

  // Funded estimate: one option per payer under the chosen funding. Do not pin
  // payoutMethod here — the details popup can browse a pending method different
  // from the committed selection, and selectedOption already filters by method.
  const fundedPreQuoteInput = useMemo<PreQuoteInput | null>(() => {
    if (!hasAmount || !funding) return null;
    return {
      originCountryCode: originCountry,
      destinationCountryCode: destinationCountry,
      amount: numericAmount,
      anchor: amountAnchor,
      destinationCurrency,
      sourceCurrency: baseSourceCurrency,
      vary: "payer",
      fundingMethod: funding,
      include: ["options", "summary"],
    };
  }, [
    hasAmount,
    funding,
    numericAmount,
    amountAnchor,
    originCountry,
    destinationCountry,
    destinationCurrency,
    baseSourceCurrency,
  ]);

  const { result: fundedPreQuote, loading: fundedPreQuoteLoading } =
    usePreQuote(fundedPreQuoteInput);

  // B: once payout+payer are pinned, best-per-funding against THIS reception.
  // pinFunding of another method clears selection and this input goes null.
  const scopedFundingPreQuoteInput = useMemo<PreQuoteInput | null>(() => {
    if (!hasAmount || !selection) return null;
    const payerCode =
      selection.payout.payerId ??
      selection.payout.carrierId ??
      selection.option?.payerCode ??
      undefined;
    return {
      originCountryCode: originCountry,
      destinationCountryCode: destinationCountry,
      amount: numericAmount,
      anchor: amountAnchor,
      destinationCurrency,
      sourceCurrency: baseSourceCurrency,
      vary: "fundingMethod",
      include: ["facets", "options", "summary"],
      payoutMethod: selection.method,
      ...(payerCode ? { payerCode } : {}),
    };
  }, [
    hasAmount,
    selection,
    numericAmount,
    amountAnchor,
    originCountry,
    destinationCountry,
    destinationCurrency,
    baseSourceCurrency,
  ]);

  const { result: scopedFundingPreQuote } = usePreQuote(
    scopedFundingPreQuoteInput
  );

  const preQuote = funding ? fundedPreQuote : discoveryPreQuote;
  const preQuoteLoading = funding
    ? fundedPreQuoteLoading
    : discoveryPreQuoteLoading;

  const sourceCurrency =
    discoveryPreQuote?.summary?.best?.sourceCurrency ??
    fundedPreQuote?.summary?.best?.sourceCurrency ??
    baseSourceCurrency;

  const discoveryOptions = useMemo(
    () => discoveryPreQuote?.options ?? [],
    [discoveryPreQuote]
  );
  const options = useMemo(() => preQuote?.options ?? [], [preQuote]);

  // Funding chips from pre-collapse facets (preferred) or discovery options.
  const availableFunding = useMemo(() => {
    const fromFacets = (discoveryPreQuote?.facets?.fundingMethods ?? []).map(
      (f) => f.fundingMethod
    );
    if (fromFacets.length > 0) {
      return availableFundingMethods(fromFacets);
    }
    return availableFundingMethods(discoveryOptions);
  }, [discoveryPreQuote, discoveryOptions]);
  const fundingPickerLoading =
    paymentMethodsLoading ||
    discoveryPreQuoteLoading ||
    (availableFunding.length === 0 &&
      !discoveryPreQuote &&
      !discoveryPreQuoteError);

  useEffect(() => {
    if (cardClassNoticeHandledRef.current) return;
    if (!cardClassNotice) return;
    if (cardClassNotice.kind === "changed" && availableFunding.length === 0) {
      return;
    }

    cardClassNoticeHandledRef.current = true;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });

    if (cardClassNotice.kind === "unclassified") {
      setBlockedFundingCard(sheetCardFromNotice(cardClassNotice.card));
      return;
    }

    if (!availableFunding.includes(cardClassNotice.fundingMethod)) {
      setFunding(null);
      setFundingInstrument(null);
      setBlockedFundingCard(sheetCardFromNotice(cardClassNotice.card));
      return;
    }

    setClassChangedToast(cardClassNotice.fundingMethod);
  }, [
    availableFunding,
    cardClassNotice,
    location.pathname,
    location.search,
    navigate,
    setFunding,
    setFundingInstrument,
  ]);

  const queriesForFundingMethod = useCallback(
    (method: FundingMethod) => {
      const customerFee = methodEstimateFee({
        method,
        scopedFacets: scopedFundingPreQuote?.facets,
        scopedOptions: scopedFundingPreQuote?.options,
        discoveryFacets: discoveryPreQuote?.facets,
        discoveryOptions: discoveryOptions,
      });
      const corridors = corridorsForFundingMethod(method, {
        options: [...discoveryOptions, ...options],
        facets: discoveryPreQuote?.facets,
      });
      const pinnedPayout = selection?.method;
      const pinnedPayer = selection
        ? selection.payout.payerId ??
          selection.payout.carrierId ??
          selection.option?.payerCode ??
          null
        : undefined;
      return skusForCorridors(method, corridors, {
        payoutMethod: pinnedPayout,
        payerCode: pinnedPayer,
        customerFee,
      });
    },
    [
      discoveryOptions,
      discoveryPreQuote?.facets,
      options,
      scopedFundingPreQuote,
      selection,
    ]
  );

  const { details: fundingDetails, loading: fundingLimitsLoading } =
    useFundingMethodLimits(
      availableFunding,
      queriesForFundingMethod,
      originCountry,
      destinationCountry,
      baseSourceCurrency
    );

  // Payout methods from the funded estimate once funding is chosen; discovery
  // facets (pre-collapse) keep route-unavailable honest before a funding pick.
  const availablePayoutMethods = useMemo<PayoutMethodType[]>(() => {
    if (funding) {
      const seen = new Set<PayoutMethodType>();
      for (const option of options) {
        if (isSupportedPayoutMethod(option.payoutMethod)) {
          seen.add(option.payoutMethod);
        }
      }
      return SUPPORTED_PAYOUT_METHODS.filter((m) => seen.has(m));
    }
    const fromFacets = (discoveryPreQuote?.facets?.payoutMethods ?? []).map(
      (p) => p.payoutMethod
    );
    if (fromFacets.length > 0) {
      return SUPPORTED_PAYOUT_METHODS.filter((m) => fromFacets.includes(m));
    }
    const seen = new Set<PayoutMethodType>();
    for (const option of discoveryOptions) {
      if (isSupportedPayoutMethod(option.payoutMethod)) {
        seen.add(option.payoutMethod);
      }
    }
    return SUPPORTED_PAYOUT_METHODS.filter((m) => seen.has(m));
  }, [options, discoveryOptions, discoveryPreQuote, funding]);

  const routeUnavailable =
    !preQuoteLoading &&
    Boolean(preQuote) &&
    availablePayoutMethods.length === 0;
  const payoutPickerLoading =
    availablePayoutMethods.length === 0 &&
    !routeUnavailable &&
    (preQuoteLoading || !preQuote);

  const handleSelectPayoutMethod = useCallback((method: PayoutMethodType) => {
    setPendingMethod(method);
    setPayoutStep("details");
    safeCapture(getEvents().PAYOUT_SELECTED, { payout_method: method });
  }, []);

  const cardBookEntries = useMemo(
    () =>
      compatibleBookDestinations(
        bookDestinations,
        PayoutMethodType.PUSH_TO_CARD,
        options
      ),
    [bookDestinations, options]
  );

  const bankBookEntries = useMemo(
    () =>
      compatibleBookDestinations(
        bookDestinations,
        PayoutMethodType.BANK_ACCOUNT,
        options
      ),
    [bookDestinations, options]
  );

  const pinBookDestination = useCallback(
    (method: PayoutMethodType, entry: CompatibleBookDestination) => {
      const option =
        options.find((o) => o.capabilityKey === entry.capabilityKey) ?? null;
      setSelection({
        method,
        option,
        payout: entry.payout,
      });
      safeCapture(getEvents().PAYOUT_SELECTED, {
        payout_method: method,
      });
      if (option) {
        safeCapture(getEvents().CAPABILITY_SELECTED, {
          capability_key: option.capabilityKey,
          payer_code: option.payerCode,
          payout_method: method,
          funding_method: option.fundingMethod,
        });
      }
      setPopup(null);
    },
    [options, setSelection]
  );

  const handlePayoutPick = useCallback(
    (pick: FlatPayoutPick) => {
      if (pick.kind === "card") {
        pinBookDestination(PayoutMethodType.PUSH_TO_CARD, pick.entry);
        return;
      }
      if (pick.kind === "bank") {
        pinBookDestination(PayoutMethodType.BANK_ACCOUNT, pick.entry);
        return;
      }
      if (pick.kind === "add") {
        handleSelectPayoutMethod(PayoutMethodType.PUSH_TO_CARD);
        return;
      }
      if (pick.kind === "addBank") {
        handleSelectPayoutMethod(PayoutMethodType.BANK_ACCOUNT);
        return;
      }
      handleSelectPayoutMethod(pick.method);
    },
    [handleSelectPayoutMethod, pinBookDestination]
  );

  const handleDetailsSubmit = useCallback(
    (result: PayoutDetailsResult) => {
      if (!pendingMethod) return;
      setSelection({
        method: pendingMethod,
        option: result.option,
        payout: result.payout,
      });
      if (result.option) {
        safeCapture(getEvents().CAPABILITY_SELECTED, {
          capability_key: result.option.capabilityKey,
          payer_code: result.option.payerCode,
          payout_method: pendingMethod,
          funding_method: result.option.fundingMethod,
        });
      }
      setPopup(null);
    },
    [pendingMethod, setSelection]
  );

  const payoutIcon = useMemo(() => {
    switch (selection?.method) {
      case PayoutMethodType.BANK_ACCOUNT:
        return <BankIcon size={20} />;
      case PayoutMethodType.CASH_PICKUP:
        return <CashSpecialIcon size={20} />;
      case PayoutMethodType.PUSH_TO_CARD:
        return <CardIcon size={20} />;
      default:
        return <WalletIcon size={20} />;
    }
  }, [selection]);

  const { data: cashPayers } = useCatalogPayers(
    selection?.method === PayoutMethodType.CASH_PICKUP && destinationCountry
      ? {
          destinationCountryCode: destinationCountry,
          payoutMethod: "CASH_PICKUP",
          ...(originCountry ? { originCountryCode: originCountry } : {}),
          ...(funding ? { fundingMethod: funding } : {}),
          ...(destinationCurrency ? { currencyCode: destinationCurrency } : {}),
        }
      : null
  );

  const payoutSummary = useMemo(() => {
    if (!funding) return t("transfer.selectFundingFirst");
    if (!selection) return t("transfer.selectPayoutMethod");
    const method = t(`payout.methods.${selection.method}`);
    const { payout } = selection;
    switch (selection.method) {
      case PayoutMethodType.BANK_ACCOUNT: {
        const last4 =
          payout.last4 ||
          payout.accountNumber?.replace(/\D/g, "").slice(-4);
        const stored =
          payout.bankId && !looksLikePayerCode(payout.bankId)
            ? payout.bankId
            : null;
        const line = formatBankAccountLabel(stored, last4);
        if (line) return line;
        return last4 ? `${method} · •••• ${last4}` : method;
      }
      case PayoutMethodType.CASH_PICKUP: {
        if (!payout.payerId) return method;
        const display = catalogPayerDisplayName(payout.payerId, cashPayers);
        // While catalog loads, show method only — avoid flashing UPPERCASE codes.
        return display ? `${method} · ${display}` : method;
      }
      case PayoutMethodType.PUSH_TO_CARD:
        // Card label parity: BRAND-0000.
        return payout.last4
          ? `${(payout.brand || "CARD").toUpperCase()}-${payout.last4}`
          : method;
      case PayoutMethodType.MOBILE_WALLET:
        return payout.phone ? `${method} · ${payout.phone}` : method;
      default:
        return method;
    }
  }, [funding, selection, t, cashPayers]);

  const fundingSummary = useMemo(() => {
    if (!funding) return t("transfer.selectFundingMethod");
    if (FUNDING_METHOD_CATEGORY[funding] === "CARD" && fundingInstrument) {
      // Card label parity: BRAND-0000.
      return `${(fundingInstrument.brand || "CARD").toUpperCase()}-${fundingInstrument.last4}`;
    }
    return t(`funding.methods.${funding}`, { defaultValue: funding });
  }, [funding, fundingInstrument, t]);

  const fundingIcon = useMemo(() => {
    if (!funding) return <WalletIcon size={20} />;
    const category = FUNDING_METHOD_CATEGORY[funding];
    if (category === "CARD" && fundingInstrument?.brand) {
      const brand = fundingInstrument.brand;
      const upper = brand.toUpperCase();
      const logo = upper.includes("VISA")
        ? visaLogo
        : upper.includes("MASTERCARD")
          ? masterCardLogo
          : null;
      if (logo) return <img src={logo} alt={`${brand} logo`} />;
    }
    if (category === "ACCOUNT") return <BankIcon size={20} />;
    if (category === "CASH") return <CashSpecialIcon size={20} />;
    return <WalletIcon size={20} />;
  }, [funding, fundingInstrument?.brand]);

  const recipientSummary = recipient
    ? recipient.fullName ||
      [recipient.firstName, recipient.lastName].filter(Boolean).join(" ")
    : t("transfer.selectRecipient");

  // Exact SKU: payout method + funding, and the chosen cash/wallet payer when
  // pinned — never the cheapest rail for the funding method alone.
  const selectedOption = useMemo<EstimateOption | null>(() => {
    if (!selection || !funding) return null;
    const payerCode =
      selection.method === PayoutMethodType.CASH_PICKUP
        ? selection.payout.payerId
        : selection.method === PayoutMethodType.MOBILE_WALLET
          ? selection.payout.carrierId
          : null;
    return resolveSelectedEstimateOption(options, {
      payoutMethod: selection.method,
      fundingMethod: funding,
      payerCode,
      preferred: selection.option,
    });
  }, [options, selection, funding]);

  const quoteCardId =
    funding &&
    FUNDING_METHOD_CATEGORY[funding] === "CARD" &&
    fundingInstrument?.source === "saved"
      ? fundingInstrument.paymentMethodId
      : null;

  const {
    quote,
    loading: quoteLoading,
    error: quoteError,
    refetch: refetchQuote,
  } = useQuote({
    sourceCountry: originCountry,
    destinationCountry,
    fundingMethod: funding,
    payoutMethod: selection?.method ?? null,
    capabilityKey: selectedOption?.capabilityKey ?? null,
    amount: hasAmount ? numericAmount : null,
    currency: sourceCurrency,
    destinationCurrency,
    anchor: amountAnchor,
    cardId: quoteCardId,
  });

  const selectedFundingDetails = funding ? fundingDetails[funding] : undefined;

  const sendForBound = hasAmount
    ? resolveComposeSendAmount({
        anchor: amountAnchor,
        pinnedAmount: numericAmount,
        optionSendAmount:
          selectedOption?.sendAmount ?? quote?.sendAmount ?? null,
      })
    : null;
  const amountBoundVerdict = compareSendToBound(
    sendForBound,
    selectedFundingDetails
  );
  const amountOutOfRange =
    amountBoundVerdict === "below_min" || amountBoundVerdict === "above_max";

  // Sequential compose: only the first incomplete step is focused; later steps
  // stay locked until prior ones are ready. CARD funding also needs an instrument.
  const fundingReady =
    funding != null &&
    (FUNDING_METHOD_CATEGORY[funding] !== "CARD" ||
      isFundingInstrumentReady(fundingInstrument));
  const payoutReady = Boolean(selection);
  const recipientReady = Boolean(recipient);

  const activeStep: ComposeStep | null = !hasAmount
    ? null
    : !fundingReady
      ? "funding"
      : !recipientReady
        ? "recipient"
        : !payoutReady
          ? "payout"
          : null;

  const isComposeActive = (step: ComposeStep) => activeStep === step;
  const isComposeReady = (step: ComposeStep) => {
    if (step === "funding") return fundingReady;
    if (step === "payout") return payoutReady;
    return recipientReady;
  };
  // Lock only incomplete future steps. A row that already has data (e.g.
  // recipient name while payout was invalidated) stays fully styled/editable.
  const isComposeLocked = (step: ComposeStep) => {
    if (!hasAmount) return true;
    if (activeStep === null) return false;
    if (isComposeReady(step)) return false;
    return (
      COMPOSE_STEP_ORDER.indexOf(step) > COMPOSE_STEP_ORDER.indexOf(activeStep)
    );
  };

  const quoteMatches = quoteMatchesSelection(quote, quoteLoading, {
    fundingMethod: funding,
    payoutMethod: selection?.method ?? null,
    capabilityKey: selectedOption?.capabilityKey,
  });
  const railsReady = hasAmount && fundingReady && payoutReady;
  const composeReady = railsReady && recipientReady;
  const quoteLoadingTimedOut = useQuoteLoadingTimedOut(quoteLoading);
  const quoteFailed = Boolean(quoteError) || quoteLoadingTimedOut;
  const skuMissing =
    railsReady && !preQuoteLoading && !selectedOption?.capabilityKey;
  const blockForPricing =
    railsReady && !quoteMatches && !quoteFailed && !skuMissing;
  const showQuoteLoading =
    (quoteLoading || blockForPricing) && !quoteFailed && !skuMissing;
  const ctaWaiting = confirmLoading || blockForPricing || quoteFailed;
  const canConfirm =
    composeReady &&
    Boolean(selectedOption?.capabilityKey) &&
    quoteMatches &&
    !routeUnavailable;
  const showRainbow =
    (canConfirm || confirmLoading) &&
    !fundingLimitsLoading &&
    !amountOutOfRange;

  const nudgeIncompleteCompose = useCallback(() => {
    setShake(true);
    window.setTimeout(() => setShake(false), 500);
    // Prefer the already-marked incomplete step; amount gap lands on the quote.
    const scrollToGap = () => {
      const target =
        document.querySelector(".estimated-quote-section.shake") ??
        document.querySelector(".payout-container .payment-required") ??
        document.querySelector(".payout-container .shake") ??
        document.querySelector(".estimated-quote-section");
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    requestAnimationFrame(scrollToGap);
    window.setTimeout(scrollToGap, 50);
  }, []);

  const handleConfirm = useCallback(
    async (cvv?: string) => {
      if (confirmLoading) return;
      if (!canSend) {
        redirectTo(APP_ROUTES.HOME_PAGE, { replace: true });
        return;
      }

      // Always allow the press: guide the user to whatever is still missing
      // instead of keeping the CTA disabled until a binding quote exists.
      if (
        !hasAmount ||
        !funding ||
        !fundingReady ||
        !selection ||
        !payoutReady ||
        !recipient ||
        !recipientReady
      ) {
        nudgeIncompleteCompose();
        return;
      }

      if (routeUnavailable) return;

      if (fundingLimitsLoading) return;
      if (amountOutOfRange) {
        setPopup("amountLimit");
        return;
      }

      // Compose is complete — wait for a quote of the selected rails (not a stale JWT).
      if (
        preQuoteLoading ||
        !selectedOption?.capabilityKey ||
        !quoteMatches ||
        !quote
      ) {
        return;
      }

      const linkId = linkIdFromRoute || session.data?.linkId;
      if (!linkId) return;

      if (FUNDING_METHOD_CATEGORY[funding] === "CARD" && fundingInstrument) {
        const cardForGate = resolveFundingCardFromInstrument(
          fundingInstrument,
          savedCards
        );
        if (
          cardForGate &&
          isFundingCardBlocked(cardForGate, availableFunding)
        ) {
          setBlockedFundingCard(cardForGate);
          return;
        }
      }

      setConfirmLoading(true);
      setPrepareError(null);
      safeCapture(getEvents().CONTINUE_CLICK);
      try {
        setQuoteState(quote);

        let preparedFundingId: string | undefined;
        let preparedPayout = selection.payout;

        if (FUNDING_METHOD_CATEGORY[funding] === "CARD" && fundingInstrument) {
          const fundingForPrepare = cvvGate?.funding ?? fundingInstrument;
          const fundingResult = await prepareFundingRail({
            linkId,
            funding: fundingForPrepare,
            capabilityKey: selectedOption.capabilityKey,
            cvv,
            ...(funding === "DEBIT_CARD" || funding === "CREDIT_CARD"
              ? { fundingMethod: funding }
              : {}),
          });

          setFundingInstrument(fundingResult.funding);
          preparedFundingId = fundingResult.funding.paymentMethodId;
          // New register or provision — keep the saved-card list in sync.
          void refreshPaymentMethods();

          if (fundingResult.status === "needs_cvv") {
            setCvvGate({
              last4: fundingResult.funding.last4,
              brand: fundingResult.funding.brand,
              funding: fundingResult.funding,
              error:
                fundingResult.errorCode &&
                fundingResult.errorCode !== "CVV_REQUIRED"
                  ? prepareErrorMessage(fundingResult.errorCode)
                  : null,
            });
            return;
          }

          if (fundingResult.status === "failed") {
            const reason = reasonFromCardPayCode(
              fundingResult.errorCode,
              fundingResult.retryLater,
            );
            safeCapture(getEvents().CARD_REGISTERED, {
              success: false,
              error_code: fundingResult.errorCode ?? "PROVISION_FAILED",
              card_brand: fundingResult.funding.brand,
              funding_payment_method_id: fundingResult.funding.paymentMethodId,
            });
            if (reason) {
              const listed = resolveFundingCardFromInstrument(
                fundingResult.funding,
                savedCards,
              );
              if (
                reason === "railRejected" ||
                reason === "railTemporarilyUnavailable"
              ) {
                const paymentMethodId = fundingResult.funding.paymentMethodId;
                setBlockedRailCards((prev) => {
                  const next = new Map(prev);
                  next.set(paymentMethodId, reason);
                  return next;
                });
                setFundingInstrument(null);
                safeCapture(getEvents().CARD_UNAVAILABLE_SHOWN, { reason });
              }
              setBlockedFundingCard(
                listed ?? {
                  paymentMethodId: fundingResult.funding.paymentMethodId,
                  last4: fundingResult.funding.last4,
                  brand: fundingResult.funding.brand,
                  expiryMonth: "",
                  expiryYear: "",
                  enabled: true,
                  isDefault: false,
                  fundingMethod: fundingResult.funding.fundingMethod,
                },
              );
              return;
            }
            setPrepareError(prepareErrorMessage(fundingResult.errorCode));
            return;
          }

          safeCapture(getEvents().CARD_REGISTERED, {
            success: true,
            card_brand: fundingResult.funding.brand,
            funding_payment_method_id: fundingResult.funding.paymentMethodId,
            last4: fundingResult.funding.last4,
          });
          setCvvGate(null);
        }

        if (selection.method === PayoutMethodType.PUSH_TO_CARD) {
          const cardholderName =
            recipient.fullName ||
            [recipient.firstName, recipient.lastName].filter(Boolean).join(" ");

          // Sandbox CVS stub only accepts stub_* ids. Recipient-book cards from
          // the pre-CVS era carry legacy ids and must be re-entered.
          const payoutPmId = selection.payout.paymentMethodId;
          const fundingLooksSandbox = preparedFundingId?.startsWith("stub_");
          const payoutLooksLegacy =
            Boolean(payoutPmId) && !payoutPmId!.startsWith("stub_");
          if (fundingLooksSandbox && payoutLooksLegacy) {
            setPrepareError(prepareErrorMessage("STALE_PAYMENT_METHOD"));
            setSelection({
              method: selection.method,
              option: selection.option,
              payout: {},
            });
            return;
          }

          if (
            !selection.payout.paymentMethodId &&
            (isPayoutCardValidationStale(
              selection.payout.validatedFor,
              originCountry,
              destinationCountry,
            ) ||
              !selection.payout.cvsValidationId)
          ) {
            setPrepareError(prepareErrorMessage("CARD_PUSH_STALE"));
            return;
          }

          const payoutResult = await preparePayoutRail({
            linkId,
            payout: selection.payout,
            capabilityKey: selectedOption.capabilityKey,
            cardholderName,
          });

          preparedPayout = sanitizePayoutAfterPrepare(payoutResult.payout);
          setSelection({
            ...selection,
            payout: preparedPayout,
          });

          if (payoutResult.status === "failed") {
            setPrepareError(prepareErrorMessage(payoutResult.errorCode));
            if (payoutResult.errorCode === "STALE_PAYMENT_METHOD") {
              setSelection({
                method: selection.method,
                option: selection.option,
                payout: {},
              });
            }
            return;
          }
        }

        setDraft({
          sourceCountry: originCountry,
          destinationCountry,
          sourceCurrency,
          destinationCurrency,
          // Draft amount is always the send leg for downstream display; the
          // binding quote carries the true pin (send or receive) for refresh.
          amount: quote.sendAmount,
          fundingMethod: funding,
          payoutMethod: selection.method,
          payout: preparedPayout,
          recipient,
          capabilityKey: selectedOption.capabilityKey,
          fundingPaymentMethodId: preparedFundingId,
        });

        const cip = await checkCipReadiness(linkId);
        if (cip.shouldRunCip && cip.cipTicketId) {
          navigate(APP_ROUTES.CIP_PAGE, {
            replace: true,
            state: { cipTicketId: cip.cipTicketId },
          });
          return;
        }
        const prompt = shouldPromptPhone(session.data, linkId);
        if (prompt) {
          redirectTo(APP_ROUTES.PHONE_PAGE, {
            replace: true,
            state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
          });
          return;
        }
        await ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en");
        redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, {
          replace: true,
          state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
        });
      } catch (error) {
        const code =
          error && typeof error === "object" && "code" in error
            ? String((error as { code?: string }).code)
            : undefined;
        if (code === "REGISTER_FAILED" || code === "VGS_CONFIG_UNAVAILABLE") {
          setPrepareError(prepareErrorMessage(code));
          return;
        }
        // Link expired / revoked: the dead-link handler already routed to the
        // screen that explains why the link stopped working.
        if (isDeadLinkError(error)) {
          return;
        }
        const apiCode = getWidgetApiError(error)?.code;
        if (apiCode === "USER_BLOCKED" || apiCode === "USER_SUSPENDED") {
          redirectTo(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: t("error.sendsDisabledTitle"),
              subTitle: null,
              message: t("error.sendsDisabledMessage"),
              btnText: t("common.needHelp"),
              isOut: false,
              pageUrl: APP_ROUTES.SUPPORT_PAGE,
            },
          });
          return;
        }
        navigate(APP_ROUTES.ERROR_PAGE, {
          state: {
            title: t("error.title"),
            subTitle: null,
            message: t("error.tryAgainLater"),
            btnText: t("common.retry"),
            isOut: false,
            pageUrl: APP_ROUTES.TRANSFER_PAGE,
          },
        });
      } finally {
        setConfirmLoading(false);
      }
    },
    [
      selection,
      funding,
      fundingReady,
      fundingInstrument,
      savedCards,
      availableFunding,
      cvvGate,
      recipient,
      recipientReady,
      payoutReady,
      selectedOption,
      quote,
      quoteMatches,
      preQuoteLoading,
      hasAmount,
      routeUnavailable,
      canSend,
      confirmLoading,
      fundingLimitsLoading,
      amountOutOfRange,
      nudgeIncompleteCompose,
      linkIdFromRoute,
      session.data?.linkId,
      originCountry,
      destinationCountry,
      sourceCurrency,
      destinationCurrency,
      numericAmount,
      setQuoteState,
      setDraft,
      setFundingInstrument,
      setSelection,
      refreshPaymentMethods,
      navigate,
      redirectTo,
      prepareErrorMessage,
      t,
      i18n,
    ]
  );

  const estimatedRate =
    quote?.exchangeRate ?? preQuote?.summary?.best?.customerFxRate;
  const rateText = estimatedRate
    ? t("exchange.rate", {
        from: sourceCurrency,
        rate: estimatedRate.toFixed(4),
        to: destinationCurrency,
      })
    : t("exchange.rate", {
        from: sourceCurrency,
        rate: "—",
        to: destinationCurrency,
      });
  const openFxDisclaimer = useCallback(() => setShowExcratePopup(true), []);
  const onFxDisclaimerKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setShowExcratePopup(true);
    }
  }, []);

  const pricingOption =
    selectedOption ?? (hasAmount ? (preQuote?.summary?.best ?? null) : null);
  const fee = quoteMatches
    ? (quote?.totalFee ?? null)
    : (pricingOption?.customerFee ?? null);
  const totalCost = quoteMatches
    ? (quote?.totalCost ?? null)
    : pricingOption
      ? pricingOption.sendAmount + pricingOption.customerFee
      : null;

  const feeDisplay = useMemo(
    () =>
      fee != null ? `$${amountFormatter(fee, sourceCurrency)}` : null,
    [fee, sourceCurrency]
  );

  const totalPaymentDisplay = useMemo(() => {
    if (totalCost != null) {
      return `$${amountFormatter(totalCost, sourceCurrency)}`;
    }
    if (hasAmount && fee != null) {
      return `$${amountFormatter(numericAmount + fee, sourceCurrency)}`;
    }
    return null;
  }, [totalCost, hasAmount, numericAmount, fee, sourceCurrency]);

  // Derived opposite-leg displays for the non-pinned QuoteInfo input.
  const receiveFormatted = useMemo(() => {
    const value =
      quote?.receiveAmount ??
      selectedOption?.receiveAmount ??
      preQuote?.summary?.best?.receiveAmount;
    if (value == null) return undefined;
    return amountFormatter(value, destinationCurrency, { country: "en-US" });
  }, [quote, selectedOption, preQuote, destinationCurrency]);

  const sendFormatted = useMemo(() => {
    const value =
      quote?.sendAmount ??
      selectedOption?.sendAmount ??
      preQuote?.summary?.best?.sendAmount;
    if (value == null) return undefined;
    return amountFormatter(value, sourceCurrency, { country: "en-US" });
  }, [quote, selectedOption, preQuote, sourceCurrency]);

  const sendAmountForWarning =
    quote?.sendAmount ??
    selectedOption?.sendAmount ??
    preQuote?.summary?.best?.sendAmount ??
    (amountAnchor === "send" && hasAmount ? numericAmount : null);

  const firstSend = useFirstSendWarning({
    linkId,
    sendAmount: sendAmountForWarning,
    sourceCurrency,
    fundingMethod: funding,
  });

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable need-help-header"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(-1)}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <section>
            <Title title={t("transfer.title")} align="left" color={TITLE_COLOR} />
            <section
              className={`estimated-quote-section stacked-quote${
                shake && !hasAmount ? " shake" : ""
              }`}
            >
              <section className="quote-container">
                <div className="banrural-amounts">
                  <div className="banrural-send-wrap">
                    <QuoteInfo
                      label={t("transfer.youSend")}
                      currency={sourceCurrency}
                      textColor={bodyCopy}
                      mode="input"
                      side="send"
                      isActive={amountAnchor === "send"}
                      derivedAmount={
                        amountAnchor === "receive" ? sendFormatted : undefined
                      }
                      showSelectorChevron
                      onSelect={() => setPopup("origin")}
                      icon={<FlagCircle iso2={originCountry.toLowerCase()} />}
                    />
                    <span
                      className="banrural-swap"
                      role="button"
                      tabIndex={0}
                      onClick={openFxDisclaimer}
                      onKeyDown={onFxDisclaimerKeyDown}
                      style={{ background: backgroundBtn }}
                    >
                      <ExchangeIcon size={18} colors={[textColorBtn, textColorBtn]} />
                    </span>
                  </div>
                  <QuoteInfo
                    label={t("transfer.youReceive")}
                    currency={destinationCurrency}
                    textColor={bodyCopy}
                    mode="input"
                    side="receive"
                    isActive={amountAnchor === "receive"}
                    derivedAmount={
                      amountAnchor === "send" ? receiveFormatted : undefined
                    }
                    hideCurrencySymbol={destinationCurrency !== "USD"}
                    showSelectorChevron
                    onSelect={() => setPopup("destination")}
                    icon={
                      <FlagCircle iso2={destinationCountry.toLowerCase()} />
                    }
                    bottomSlot={
                      <section
                        className="banrural-exchange-rate banrural-exchange-rate--alert"
                        role="button"
                        tabIndex={0}
                        onClick={openFxDisclaimer}
                        onKeyDown={onFxDisclaimerKeyDown}
                        style={
                          {
                            "--bgc": bannerHighlightBackground ?? bodyCopy,
                            "--colorc": solidStop(
                              bannerHighlightIconColors?.[0] ?? title,
                            ),
                            "--fillc": bannerHighlightColor ?? title,
                          } as CSSProperties
                        }
                      >
                        <span className="banrural-exchange-rate-icon">i</span>
                        <span className="banrural-exchange-rate-text">
                          <b>{rateText}</b>
                          <label>{t("exchange.estimatedRate")}</label>
                        </span>
                      </section>
                    }
                  />
                </div>
              </section>
            </section>

            <section className="payout-container">
              <PayoutInfo
                title={t("transfer.payWith")}
                subtitle={fundingSummary}
                icon={fundingIcon}
                background={backgroundBtn}
                color={textColorBtn}
                locked={isComposeLocked("funding")}
                onChange={() => {
                  void refreshPaymentMethods();
                  setPopup("funding");
                }}
                className={`${isComposeActive("funding") ? "payment-required" : ""} ${
                  shake && hasAmount && !fundingReady ? "shake" : ""
                }`.trim()}
              />

              <PayoutInfo
                title={t("transfer.recipient")}
                subtitle={recipientSummary}
                icon={<WalletIcon size={20} />}
                background={backgroundBtn}
                color={textColorBtn}
                locked={isComposeLocked("recipient")}
                onChange={() => setPopup("recipient")}
                className={`${isComposeActive("recipient") ? "payment-required" : ""} ${
                  shake && hasAmount && !recipientReady ? "shake" : ""
                }`.trim()}
              />

              <PayoutInfo
                title={t("transfer.receiveIn")}
                subtitle={payoutSummary}
                icon={payoutIcon}
                background={backgroundBtn}
                color={textColorBtn}
                locked={isComposeLocked("payout")}
                onChange={() => {
                  setPayoutStep("method");
                  setPendingMethod(null);
                  setPopup("payout");
                }}
                className={`${isComposeActive("payout") ? "payment-required" : ""} ${
                  shake && hasAmount && !payoutReady ? "shake" : ""
                }`.trim()}
              />

              {firstSend.visible ? (
                <FirstSendWarning title={firstSend.title} body={firstSend.body} />
              ) : null}

              <BodyCopy className="footer-text transfer-timing-note" textColor={bodyCopy}>
                <b>{t("transfer.instantTransfer")}</b>
                {" "}
                <span className="transfer-timing-note-rest">
                  {t("transfer.mayTakeFew")}
                </span>
              </BodyCopy>

              {routeUnavailable ? (
                <BodyCopy className="footer-text" textColor={bodyCopy}>
                  <b>{t("transfer.routeUnavailableTitle")}</b>
                  <br />
                  {t("transfer.routeUnavailableHint")}
                </BodyCopy>
              ) : null}
              {prepareError ? (
                <BodyCopy className="footer-text" textColor={bodyCopy}>
                  {prepareError}
                </BodyCopy>
              ) : null}
              {skuMissing ? (
                <BodyCopy className="payment-error-text" role="alert">
                  {t("transfer.paymentMethodUnavailable")}
                </BodyCopy>
              ) : null}
              {quoteFailed && !skuMissing ? (
                <>
                  <BodyCopy className="payment-error-text" role="alert">
                    {t("transfer.quoteError")}
                  </BodyCopy>
                  <Button
                    variant="cancel"
                    size="small"
                    color={bodyCopy}
                    onClick={() => {
                      void refetchQuote();
                    }}
                  >
                    {t("transfer.quoteRetry")}
                  </Button>
                </>
              ) : null}
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <FooterDetailSummary
              heading={t("transfer.detail")}
              fee={t("transfer.fee", {
                amount: feeDisplay ?? `$${amountFormatter(0, sourceCurrency)}`,
                currency: sourceCurrency,
              })}
              total={t("transfer.totalPayment", {
                amount:
                  totalPaymentDisplay ??
                  `$${amountFormatter(0, sourceCurrency)}`,
                currency: sourceCurrency,
              })}
              titleColor={TITLE_COLOR}
              textColor={TITLE_COLOR}
            />
            <div className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}>
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={ctaWaiting}
                onClick={() => void handleConfirm()}
              >
                {confirmLoading
                  ? t("transfer.prepare.loading")
                  : showQuoteLoading
                    ? t("transfer.loadingQuote")
                    : t("transfer.send")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>

      <Popup
        visible={popup === "amountLimit" && amountOutOfRange}
        onClose={() => setPopup(null)}
      >
        <AmountOutOfRangeInfo
          kind={amountBoundVerdict === "below_min" ? "below_min" : "above_max"}
          method={t(`funding.methods.${funding ?? ""}`, {
            defaultValue: funding ?? "",
          })}
          bound={
            amountBoundVerdict === "below_min"
              ? (selectedFundingDetails?.minAmount ?? 0)
              : (selectedFundingDetails?.maxAmount ?? 0)
          }
          currency={sourceCurrency}
          onClose={() => setPopup(null)}
        />
      </Popup>

      <Popup visible={popup === "payout"} onClose={() => setPopup(null)}>
        {payoutStep === "method" ? (
          <SelectPayoutMethodSlot
            selectedMethod={selection?.method ?? null}
            selectedPaymentMethodId={
              selection?.method === PayoutMethodType.PUSH_TO_CARD ||
              selection?.method === PayoutMethodType.BANK_ACCOUNT
                ? selection.payout.paymentMethodId ?? null
                : null
            }
            available={availablePayoutMethods}
            cardEntries={cardBookEntries}
            bankEntries={bankBookEntries}
            loading={payoutPickerLoading}
            onPick={handlePayoutPick}
            onClose={() => setPopup(null)}
          />
        ) : (
          <PayoutDetailsSlot
            method={pendingMethod as PayoutMethodType}
            destinationCountry={destinationCountry}
            destinationCurrency={destinationCurrency}
            fundingMethod={funding}
            originCountryCode={originCountry}
            options={options}
            optionsLoading={preQuoteLoading}
            bookDestinations={bookDestinations}
            value={
              selection && selection.method === pendingMethod
                ? selection.payout
                : null
            }
            onBack={() => setPayoutStep("method")}
            onClose={() => setPopup(null)}
            onSubmit={handleDetailsSubmit}
          />
        )}
      </Popup>

      <Popup visible={popup === "origin"} onClose={() => setPopup(null)}>
        <SelectOriginCountrySlot
          countries={originCountriesForPicker}
          selected={originCountry}
          onSelect={handleOriginChange}
          onClose={() => setPopup(null)}
        />
      </Popup>

      <Popup visible={popup === "destination"} onClose={() => setPopup(null)}>
        <SelectDestinationCountrySlot
          countries={destinationCountriesForPicker}
          selectedCountry={destinationCountry}
          selectedCurrency={destinationCurrency}
          onSelect={handleDestinationChange}
          onClose={() => setPopup(null)}
        />
      </Popup>

      <Popup visible={popup === "recipient"} onClose={() => setPopup(null)}>
        <RecipientDetailsSlot
          value={recipient}
          destinationCountry={destinationCountry}
          onClose={() => setPopup(null)}
          onSubmit={(r, destinations) => {
            setRecipient(r);
            safeCapture(getEvents().RECIPIENT_NAME_SUBMIT, { success: true });
            // `undefined` keeps prior book habits (re-submit same name);
            // `[]` clears them for a brand-new free-text recipient.
            if (destinations !== undefined) {
              setBookDestinations(destinations);
              // Drop selection only when it pins a saved instrument that is
              // not in the new recipient's book. Cash / no-pmId stays armed.
              if (
                shouldClearPayoutOnRecipientChange(
                  selection?.payout,
                  destinations
                )
              ) {
                setSelection(null);
              }
            }
            setPopup(null);
          }}
        />
      </Popup>

      {blockedFundingCard ? (
        <Popup visible onClose={() => setBlockedFundingCard(null)}>
          <UnavailableFundingCardSheet
            card={blockedFundingCard}
            available={availableFunding}
            bodyCopy={bodyCopy}
            backgroundBtn={backgroundBtn}
            textColorBtn={textColorBtn}
            onDismiss={() => setBlockedFundingCard(null)}
            showHeaderClose={false}
            reason={
              blockedFundingCard
                ? blockedRailCards.get(blockedFundingCard.paymentMethodId)
                : undefined
            }
            onChangeMethod={
              blockedFundingCard &&
              blockedRailCards.has(blockedFundingCard.paymentMethodId)
                ? () => {
                    setBlockedFundingCard(null);
                    setPopup("funding");
                  }
                : undefined
            }
          />
        </Popup>
      ) : null}
      {classChangedToast ? (
        <Toast
          type="info"
          duration={4000}
          message={t(
            classChangedToast === "CREDIT_CARD"
              ? "payment.classChangedCredit"
              : "payment.classChangedDebit"
          )}
          onClose={() => setClassChangedToast(null)}
        />
      ) : null}
      <Popup visible={popup === "funding"} onClose={() => setPopup(null)}>
        <SelectFundingMethodSlot
          selectedMethod={funding}
          selectedPaymentMethodId={
            (fundingInstrument?.source === "saved"
              ? fundingInstrument.paymentMethodId
              : null) || null
          }
          available={availableFunding}
          cards={savedCards}
          bankAccounts={bankAccounts}
          loading={fundingPickerLoading}
          details={fundingDetails}
          hasQuote={quoteMatches}
          quoteFee={quoteMatches ? (quote?.totalFee ?? 0) : null}
          composedOptionFee={
            selection && selectedOption ? selectedOption.customerFee : null
          }
          onPick={handleFundingPick}
          onClose={() => setPopup(null)}
          blockedRailCards={blockedRailCards}
        />
      </Popup>

      {cvvGate ? (
        <Popup
          visible
          onClose={() => {
            setCvvGate(null);
            setPrepareError(null);
          }}
        >
          <CvvPrepareGate
            last4={cvvGate.last4}
            brand={cvvGate.brand}
            loading={confirmLoading}
            error={cvvGate.error}
            onClose={() => {
              setCvvGate(null);
              setPrepareError(null);
            }}
            onSubmit={(cvv) => {
              void handleConfirm(cvv);
            }}
          />
        </Popup>
      ) : null}

      <Popup
        visible={showExcratePopup}
        onClose={() => setShowExcratePopup(false)}
      >
        <section className="qoute-popup">
          <WarningIcon size={60} />
          <BodyCopy textColor={bodyCopy}>
            <Trans i18nKey="transfer.quote.disclaimer" />
          </BodyCopy>
        </section>
      </Popup>
      {supportSheet}
    </>
  );
});
